import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSystemPresence } from "@/hooks/useSystemPresence";
import { useUnreadEmails } from "@/hooks/useUnreadEmails";
import { useSolicitudesVenta } from "@/hooks/useSolicitudesVenta";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { Button } from "@/components/ui/button";

import { CentroNotificaciones } from "@/components/CentroNotificaciones";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { LogOut, Home } from "lucide-react";
import { AlmasaLoading } from "@/components/brand/AlmasaLoading";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { es } from "date-fns/locale";
import { AlmasaLogo } from "@/components/brand/AlmasaLogo";
import { COMPANY_DATA } from "@/constants/companyData";

// Sidebar & Navigation
import { SecretariaSidebar } from "@/components/secretaria/SecretariaSidebar";
import { SecretariaMobileNav } from "@/components/secretaria/SecretariaMobileNav";
import { SecretariaBienvenidaDialog } from "@/components/secretaria/SecretariaBienvenidaDialog";

// Tab Components
import { SecretariaProductosTab } from "@/components/secretaria/SecretariaProductosTab";
import { SecretariaCostosTab } from "@/components/secretaria/SecretariaCostosTab";
import { SecretariaListaPreciosTab } from "@/components/secretaria/SecretariaListaPreciosTab";
import { SecretariaPedidosTab } from "@/components/secretaria/SecretariaPedidosTab";
import Compras from "@/pages/Compras";
import { SecretariaInventarioTab } from "@/components/secretaria/SecretariaInventarioTab";
import { SecretariaFacturacionTab } from "@/components/secretaria/SecretariaFacturacionTab";
import { MessageSquare } from "lucide-react";
import { SecretariaCorreosTab } from "@/components/secretaria/SecretariaCorreosTab";
import { SecretariaClientesTab } from "@/components/secretaria/SecretariaClientesTab";
import { SecretariaRutasTab } from "@/components/secretaria/SecretariaRutasTab";
import { SecretariaPagosValidarTab } from "@/components/secretaria/SecretariaPagosValidarTab";
import { SolicitudesAlmacenTab } from "@/components/facturas/SolicitudesAlmacenTab";

const SecretariaPanel = () => {
  const [activeTab, setActiveTab] = useState("pedidos");
  const [user, setUser] = useState<any>(null);
  const [userName, setUserName] = useState("");
  const [showBienvenida, setShowBienvenida] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const navigate = useNavigate();
  const { roles, isLoading: rolesLoading, hasRole } = useUserRoles();
  
  // Track presence in secretaria panel
  useSystemPresence('secretaria');
  
  const { totalUnread: totalUnreadEmails } = useUnreadEmails();
  const { pendingCount: ventasMostradorPendientes } = useSolicitudesVenta();
  const chatUnreadCount = useUnreadMessages();

  // Verify session and show welcome dialog
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUser(session.user);
        // Get profile name
        supabase
          .from("profiles")
          .select("full_name")
          .eq("id", session.user.id)
          .single()
          .then(({ data }) => {
            const name = data?.full_name || session.user.email?.split("@")[0] || "Secretaria";
            setUserName(name);
            
            // Check if should show welcome dialog (first visit of the day)
            const today = new Date().toDateString();
            const lastVisit = localStorage.getItem("secretaria_last_visit");
            if (lastVisit !== today) {
              setShowBienvenida(true);
              localStorage.setItem("secretaria_last_visit", today);
            }
          });
      }
    });
  }, [navigate]);

  // KPIs Query - Pedidos por autorizar
  const { data: pedidosPorAutorizar = 0 } = useQuery({
    queryKey: ["kpi-pedidos-por-autorizar"],
    queryFn: async () => {
      const { count } = await supabase
        .from("pedidos")
        .select("*", { count: "exact", head: true })
        .eq("status", "por_autorizar");
      return count || 0;
    },
    refetchInterval: 30000,
  });

  // KPIs Query - Facturas pendientes
  const { data: facturasPendientes = 0 } = useQuery({
    queryKey: ["kpi-facturas-pendientes"],
    queryFn: async () => {
      const { count } = await supabase
        .from("facturas")
        .select("*", { count: "exact", head: true })
        .is("cfdi_uuid", null);
      return count || 0;
    },
    refetchInterval: 30000,
  });

  // KPIs Query - OC por enviar
  const { data: comprasPendientes = 0 } = useQuery({
    queryKey: ["kpi-compras-pendientes"],
    queryFn: async () => {
      const { count } = await supabase
        .from("ordenes_compra")
        .select("*", { count: "exact", head: true })
        .eq("status", "autorizada");
      return count || 0;
    },
    refetchInterval: 30000,
  });

  // KPIs Query - Pagos por validar
  const { data: pagosValidar = 0 } = useQuery({
    queryKey: ["kpi-pagos-validar"],
    queryFn: async () => {
      const { count } = await supabase
        .from("pagos_cliente")
        .select("*", { count: "exact", head: true })
        .eq("status", "pendiente")
        .eq("requiere_validacion", true);
      return count || 0;
    },
    refetchInterval: 30000,
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (rolesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <AlmasaLoading size={48} text="Cargando panel..." />
      </div>
    );
  }

  const counters = {
    pedidos: pedidosPorAutorizar,
    mostrador: ventasMostradorPendientes,
    facturas: facturasPendientes,
    chat: chatUnreadCount,
    correos: totalUnreadEmails,
    compras: comprasPendientes,
    pagosValidar: pagosValidar,
  };

  const hasMultipleRoles = hasRole("admin") || roles.length > 1;

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "productos":
        return <SecretariaProductosTab />;
      case "costos":
        return <SecretariaCostosTab />;
      case "precios":
        return <SecretariaListaPreciosTab />;
      case "pedidos":
        return <SecretariaPedidosTab />;
      case "mostrador":
        return <SolicitudesAlmacenTab />;
      case "compras":
        return <Compras mode="secretaria" />;
      case "inventario":
        return <SecretariaInventarioTab />;
      case "facturacion":
        return <SecretariaFacturacionTab />;
      case "chat":
        return (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <MessageSquare className="h-12 w-12 text-ink-300 mb-4" />
            <h3 className="font-serif text-2xl text-ink-900 mb-2">Chat completo en su propia vista.</h3>
            <p className="text-ink-500 mb-6 max-w-md">
              Accede a todas las conversaciones, archivos y notificaciones desde el chat central.
            </p>
            <Button onClick={() => navigate("/chat")} className="bg-crimson-500 text-white hover:bg-crimson-600">
              Abrir chat
            </Button>
          </div>
        );
      case "correos":
        return <SecretariaCorreosTab />;
      case "clientes":
        return <SecretariaClientesTab />;
      case "rutas":
        return <SecretariaRutasTab />;
      case "pagos_validar":
        return <SecretariaPagosValidarTab />;
      default:
        return <SecretariaPedidosTab />;
    }
  };

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
      <div className="min-h-screen bg-background flex flex-col lg:flex-row w-full">
        {/* Desktop Sidebar */}
        <SecretariaSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onLogout={() => setShowLogoutDialog(true)}
          onNavigateDashboard={() => navigate("/dashboard")}
          userName={userName}
          counters={counters}
          hasMultipleRoles={hasMultipleRoles}
        />

        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-50 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground px-4 py-3 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlmasaLogo size={32} color="white" />
              <div className="border-l border-primary-foreground/30 pl-3">
                <p className="text-sm font-medium truncate max-w-[120px]">{userName}</p>
                <p className="text-[10px] opacity-70">Secretaria</p>
                <p className="text-[9px] opacity-60 italic hidden sm:block">"{COMPANY_DATA.slogan}"</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <SidebarTrigger className="text-primary-foreground hover:bg-primary-foreground/20 rounded-full" />
              <CentroNotificaciones />
              {hasMultipleRoles && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/dashboard")}
                  className="text-primary-foreground hover:bg-primary-foreground/20 rounded-full"
                >
                  <Home className="h-5 w-5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowLogoutDialog(true)}
                className="text-primary-foreground hover:bg-primary-foreground/20 rounded-full"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1" onClick={() => sidebarOpen && setSidebarOpen(false)}>
          <div className="p-4 lg:p-8 pb-32 md:pb-8">
            {/* Desktop topbar — compact */}
            <div className="hidden md:flex items-center gap-3 mb-6">
              <SidebarTrigger className="h-8 w-8 shrink-0" />
              <div className="ml-auto flex items-center gap-3">
                <CentroNotificaciones />
                <span className="text-xs text-ink-500">{user?.email}</span>
              </div>
            </div>

            {renderTabContent()}
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <SecretariaMobileNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          counters={counters}
          onLogout={() => setShowLogoutDialog(true)}
        />

        {/* Welcome Dialog */}
        <SecretariaBienvenidaDialog
          open={showBienvenida}
          onOpenChange={setShowBienvenida}
          secretariaNombre={userName}
          onNavigate={setActiveTab}
        />
      </div>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
            <AlertDialogDescription>Se cerrará tu sesión en el sistema</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: "destructive" })} onClick={handleLogout}>
              Sí, cerrar sesión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default SecretariaPanel;
